import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { AmplitudeUserProperties } from "@lifeat/shared/amplitude/AmplitudeUserProperties";
import { UserCollection, UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import { ProSubscriptionType } from "@lifeat/shared/stripe/StripeSubscriptionTypes";
import { getCurrentPrice } from "@lifeat/shared/stripe/StripeSubscriptions";
import { ProxiedRoutes, Routes } from "@lifeat/shared/system/RouteConstants";
import { PRO_SLACK_COMMUNITY_INVITE_LINK } from "@lifeat/shared/user/UserConstants";
import { isUserBusinessUserWithTeam } from "@lifeat/shared/user/UserUtil";
import * as StringUtil from "@lifeat/shared/util/StringUtil";
import { isValidString } from "@lifeat/shared/util/StringUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import LifeAtMultiLineTextInput from "@common/LifeAtTextInput/LifeAtMultiLineTextInput";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import { useDatabaseContext } from "@contexts/DatabaseContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import useOnUserDataLoaded from "@hooks/useOnUserDataLoaded";
import useQueryParam from "@hooks/useQueryParam";
import ProSlackBtnImg from "@icons/Slack_Icon_colorized.svg";
import InfoPageTemplate from "@pages/templates/InfoPageTemplate";
import { logError, logWarn } from "@service/ConsoleLoggerService";
import {
    CompleteProSubUpgradeFlowEvent,
    SendUpgradeDataToGtagEvent,
    SubmitUpgradeReasonEvent,
} from "@user/AmplitudeProSubsEvents";
import { BrowserUtil } from "@util/index";

export default function UpgradeSuccessPage() {
    return (
        <InfoPageTemplate>
            <UpgradeSuccessContents />
        </InfoPageTemplate>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 8% 20%;
`;

const PageHeader = styled.h2`
    color: ${(props) => props.theme.textColorPrimary};
`;

const ButtonTextBlock = styled.div`
    margin: 10px 0;
    color: ${(props) => props.theme.textColorPrimary};
`;

const TextBlock = styled.div`
    margin: 18px 0;
    color: ${(props) => props.theme.textColorPrimary};
`;

const ContinueBtnBlock = styled.div`
    margin: 40px 0;
`;

const PLANNER_TUTORIAL_HELPDESK_URL = "https://lifeat.gitbook.io/lifeat";
export function UpgradeSuccessContents() {
    const history = useHistory();
    const amplitudeService = AmplitudeService.getInstance();
    const { user, userData }: UserContextType = useUserContext();
    const query = useQueryParam();

    const showOnboardingStep = isUserBusinessUserWithTeam(userData?.additionalUserSuppliedData);

    useOnUserDataLoaded((loadedUserData: UserEntity) => {
        if (loadedUserData) {
            const subscriptionTier: ProSubscriptionType = query.get("tier") as ProSubscriptionType;
            const checkoutSessionId = query.get("session_id");

            if (subscriptionTier && checkoutSessionId && StringUtil.isValidString(checkoutSessionId)) {
                let value: number;
                switch (subscriptionTier) {
                    case "monthly":
                        value = getCurrentPrice("monthly");
                        break;
                    case "annual":
                        value = getCurrentPrice("annual");
                        break;
                    default:
                        value = 0.0;
                        break;
                }
                window.gtag("event", "conversion", {
                    send_to: "AW-10946739723/6pmTCJbNooEYEIv85-Mo",
                    value: value,
                    currency: "USD",
                    transaction_id: checkoutSessionId,
                });

                amplitudeService.logEvent(new SendUpgradeDataToGtagEvent(value, subscriptionTier, checkoutSessionId));
            }

            const proUserInfo = loadedUserData.proUserInfo;
            if (proUserInfo) {
                if (proUserInfo.isProUser && proUserInfo.stripeInfo) {
                    amplitudeService.logEvent(
                        new CompleteProSubUpgradeFlowEvent({
                            accountCreationDate: user.metadata.creationTime,
                        }),
                    );
                }
            } else {
                logWarn("ProUserInfo is not defined. Cannot log event to Amplitude.");
            }
        } else {
            logWarn("Info missing to log events in Google Tag Manager and Amplitude");
        }
    });

    return (
        <Container>
            <PageHeader>{"Welcome to LifeAt Pro"}</PageHeader>

            <ButtonTextBlock>
                <TextBlock>{"1. Please share the main reason(s) you upgraded to Pro"}</TextBlock>
                <UpgradeReasonInputForm />
            </ButtonTextBlock>

            {showOnboardingStep && (
                <ButtonTextBlock>
                    <TextBlock>
                        <span>
                            {"2. Complete your FREE 15 minute onboarding with a LifeAt specialist to set " +
                                "up your planner & see how you can organize your business or team ($250 value!)"}
                        </span>
                    </TextBlock>
                    <LifeAtButtonV2
                        variant={"outline"}
                        onClick={() => BrowserUtil.openLinkNewTab(ProxiedRoutes.PlannerOnboarding)}
                    >
                        {"👯‍Book a time here"}
                    </LifeAtButtonV2>
                </ButtonTextBlock>
            )}

            <ButtonTextBlock>
                <TextBlock>
                    <span>{`${showOnboardingStep ? "3" : "2"}. View a quick tutorial on how to 
                        use the LifeAt Planner and other Pro features`}</span>
                </TextBlock>
                <LifeAtButtonV2
                    variant={"outline"}
                    onClick={() => BrowserUtil.openLinkNewTab(PLANNER_TUTORIAL_HELPDESK_URL)}
                >
                    {"Watch tutorial ✨"}
                </LifeAtButtonV2>
            </ButtonTextBlock>

            <ContinueBtnBlock>
                <LifeAtButtonV2
                    overrideTextColor={"#FFFFFF"}
                    overrideBackgroundColor={"#333333"}
                    size={"large"}
                    variant={"solid"}
                    onClick={() => history.push(`/${Routes.App}`)}
                    disableHover={true}
                >
                    {"Continue to LifeAt"}
                </LifeAtButtonV2>
            </ContinueBtnBlock>
        </Container>
    );
}

const InputWrapper = styled.div``;

interface UpgradeReasonInputFormProps {}
function UpgradeReasonInputForm(props: UpgradeReasonInputFormProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const db = useDatabaseContext();
    const { user, userData }: UserContextType = useUserContext();

    const [upgradeInputError, setUpgradeInputError] = useState("");

    const updateUserUpgradeReason = (s: string) => {
        if (s !== upgradeReason) {
            handleUpdateCancelFeedbackState(s);
            const node = UserCollection.at(user.uid)
                .at("proUserInfo")
                .at("additionalSubscriptionInfo")
                .at("upgradeReason").node;
            db.set(node, upgradeReason)
                .then(() => {
                    amplitudeService.setUserProp(AmplitudeUserProperties.SUBSCRIPTION_UPGRADE_REASON, upgradeReason);
                    amplitudeService.logEvent(new SubmitUpgradeReasonEvent(upgradeReason));
                })
                .catch((e) => {
                    logError(`Error saving upgradeReason [${upgradeReason}]: ${getErrorMessage(e)}`);
                });
        }
    };

    const [upgradeReason, setUpgradeReason] = useDebouncedInput(
        userData?.proUserInfo?.additionalSubscriptionInfo?.upgradeReason ?? "",
        updateUserUpgradeReason,
        1000,
    );

    function isInputValid(str: string): boolean {
        return isValidString(str) && str.length >= 6 && str.length <= 4000;
    }

    const handleUpdateCancelFeedbackState = (s: string) => {
        if (isInputValid(s)) {
            setUpgradeInputError("");
        } else {
            setUpgradeInputError("Please enter a few words to share with us :-) we read all submissions");
        }
        setUpgradeReason(s);
    };

    return (
        <InputWrapper>
            <LifeAtMultiLineTextInput
                textValue={upgradeReason}
                onInputChange={handleUpdateCancelFeedbackState}
                handleUpdate={setUpgradeReason}
                errorMessage={upgradeInputError}
                minHeightPx={60}
                maxHeightPx={200}
                placeholder={"Enter your response here"}
                showBorder={true}
            />
        </InputWrapper>
    );
}
